import { Injectable } from '@angular/core'
import { CookieService } from 'ngx-cookie-service'
import { CountryBarService } from '../country-bar/country-bar.service'
import { CountryLanguageService } from '../../../site-context/facade/country-language.service'
import { MbConstants } from '../../../shared/mb-constants'
import { CookieKeys } from '../../enums/cookie-keys'
import { User } from '@spartacus/core'

@Injectable({
  providedIn: 'root'
})
export class CountryRedirectService {

  constructor (private cookieService: CookieService,
               private countryBarService: CountryBarService,
               private countryLanguageService: CountryLanguageService) {
  }

  /**
   * Sets all cookies to user country if present, otherwise to fallback country and removes countryBar and return contextUrl.
   *
   * @param user
   * @param url
   * @returns userCountry context param with url => e.g. UN-en/url
   */
  handleUserContext (user: User, url: string): string {
    let userCountry = user?.country?.isocode ?? MbConstants.FALLBACK_COUNTRY
    const baseSiteOfUserCountry = this.countryLanguageService.getBaseSiteForCountry(userCountry)

    this.cookieService.set(CookieKeys.MILTENYI_COUNTRY, baseSiteOfUserCountry ? userCountry : MbConstants.FALLBACK_COUNTRY, { path: '/' })
    this.cookieService.set(CookieKeys.MILTENYI_SELECT_COUNTRY, baseSiteOfUserCountry ? userCountry
      : MbConstants.FALLBACK_COUNTRY, { path: '/' })
    this.cookieService.set(CookieKeys.NAVIGATION_COUNTRY, baseSiteOfUserCountry ? userCountry : MbConstants.FALLBACK_COUNTRY, { path: '/' })

    if (!baseSiteOfUserCountry) {
      userCountry = MbConstants.FALLBACK_COUNTRY
    }

    this.countryBarService.removeCountryBar()
    return `${userCountry}-${MbConstants.FALLBACK_LANGUAGE}${url}`
  }

  /**
   * Sets cookies NAVIGATION_COUNTRY to given activeCountry and MILTENYI_COUNTRY to given selectedCountry if
   * miltenyiCountry === activeCountry, otherwise shows countryBar and returns contextUrl.
   *
   * @param contextRoutePart
   * @param url
   * @param activeCountry
   * @param miltenyiSelectCountry
   * @returns contextRoutePart + url => e.g. UN-en/url
   */
  handleNoSelectedCountry (contextRoutePart: string, url: string, activeCountry: string, miltenyiSelectCountry: string): string {
    if (miltenyiSelectCountry === activeCountry) {
      this.cookieService.set(CookieKeys.NAVIGATION_COUNTRY, activeCountry, { path: '/' })
      this.cookieService.set(CookieKeys.MILTENYI_COUNTRY, miltenyiSelectCountry, { path: '/' })
    } else {
      this.countryBarService.showCountryBar()
    }
    return `${contextRoutePart}${url}`
  }

  /***
   * Sets cookies NAVIGATION_COUNTRY if miltenyiCountry !== miltenyiSelectedCountry.
   * Shows countryBar if miltenyiCountry !== navigationCountry || miltenyiCountry !== miltenyiSelectCountry, otherwise
   * deletes cookie NAVIGATION_COUNTRY and return contextUrl.
   *
   * @param contextRoutePart
   * @param url
   * @param miltenyiCountry
   * @param miltenyiSelectCountry
   * @param activeCountry
   * @param navigationCountry
   * @returns contextRoutePart + url => e.g. UN-en/url
   */
  handleSelectedCountryIsActiveCountry (contextRoutePart: string, url: string, miltenyiCountry: string, miltenyiSelectCountry: string,
                                        activeCountry: string, navigationCountry: string): string {
    if (miltenyiCountry !== miltenyiSelectCountry) {
      this.cookieService.set(CookieKeys.NAVIGATION_COUNTRY, activeCountry, { path: '/' })
    }
    if (miltenyiCountry !== navigationCountry || miltenyiCountry !== miltenyiSelectCountry) {
      this.countryBarService.showCountryBar()
    } else {
      this.cookieService.delete(CookieKeys.NAVIGATION_COUNTRY, '/')
    }
    return `${contextRoutePart}${url}`
  }

  /**
   * Sets cookie NAVIGATION_COUNTRY to given activeCountry if not already set.
   *
   * @param activeCountry
   */
  setNavigationCountryCookieIfNotPresent (activeCountry: string): void {
    if (!this.cookieService.get(CookieKeys.NAVIGATION_COUNTRY)) {
      this.cookieService.set(CookieKeys.NAVIGATION_COUNTRY, activeCountry, { path: '/' })
    }
  }

  /***
   * Sets cookies MILTENYI_COUNTRY and MILTENYI_SELECT_COUNTRY to FALLBACK_COUNTRY if their current value has no available shop.
   */
  setCookieFallbackValuesIfNoShop (): void {
    const miltenyiCountry = this.cookieService.get(CookieKeys.MILTENYI_COUNTRY)
    if (miltenyiCountry && !this.countryLanguageService.getBaseSiteForCountry(miltenyiCountry)) {
      this.cookieService.set(CookieKeys.MILTENYI_COUNTRY, MbConstants.FALLBACK_COUNTRY, { path: '/' })
    }
    const miltenyiSelectCountry = this.cookieService.get(CookieKeys.MILTENYI_SELECT_COUNTRY)
    if (miltenyiSelectCountry && !this.countryLanguageService.getBaseSiteForCountry(miltenyiSelectCountry)) {
      this.cookieService.set(CookieKeys.MILTENYI_SELECT_COUNTRY, MbConstants.FALLBACK_COUNTRY, { path: '/' })
    }
  }

  /***
   * Shows countryBar if no activeCountry given and returns contextUrl.
   *
   * @param url
   * @param miltenyiCountry
   * @param activeCountry
   * @returns miltenyiCountry + FALLBACK_LANGUAGE + url => UN-en/url
   */
  handleMiltenyiCountryIsNotActiveCountry (url: string, miltenyiCountry: string, activeCountry: string): string {
    if (!activeCountry) {
      this.countryBarService.showCountryBar()
    }
    return `${miltenyiCountry}-${MbConstants.FALLBACK_LANGUAGE}${url}`
  }

  /***
   * Shows countryBar and returns contextUrl.
   *
   * @param miltenyiCountry
   * @param url
   * @returns miltenyiCountry + FALLBACK_LANGUAGE + url => UN-en/url
   */
  handleMiltenyiCountryIsNotNavigationCountry (miltenyiCountry: string, url: string): string {
    this.countryBarService.showCountryBar()
    return `${miltenyiCountry}-${MbConstants.FALLBACK_LANGUAGE}${url}`
  }

  /***
   * Hides countryBar and returns contextUrl based on available cookies.
   *
   * @param contextRoutePart
   * @param url
   * @param miltenyiCountry
   * @param miltenyiSelectCountry
   * @returns COOKIES + FALLBACK_LANGUAGE + url => UN-en/url
   */
  handleNoActiveCountry (contextRoutePart: string, url: string, miltenyiCountry: string, miltenyiSelectCountry: string) {
    this.countryBarService.hideCountryBar()
    if (contextRoutePart?.match(/[A-Z]{2}-[a-z]{2}/)?.length) {
      return `${contextRoutePart}${url}`
    }
    if (miltenyiCountry?.length) {
      return `${miltenyiCountry}-${MbConstants.FALLBACK_LANGUAGE}${url}`
    }
    if (miltenyiSelectCountry?.length) {
      return `${miltenyiSelectCountry}-${MbConstants.FALLBACK_LANGUAGE}${url}`
    }
    return `${MbConstants.FALLBACK_COUNTRY}-${MbConstants.FALLBACK_LANGUAGE}${url}`
  }
}
